import React, { useState, useMemo, useId, useRef, useCallback } from 'react';
import { AvatarNext as Avatar, FocusRing, LoadingSpinner, SearchInput } from '@momentum-ui/react-collaboration';
import { useTranscript } from './useTranscript';
import { useTranscriptAvatars } from './useTranscriptAvatars';
import { useTranslation } from 'react-i18next';

interface ICallRecapTranscriptTabProps {
  containerId: string;
}

const TEXT1 = 'var(--mds-color-theme-text-primary-normal)';
const TEXT2 = 'var(--mds-color-theme-text-secondary-normal)';
const BORDER = 'var(--mds-color-theme-outline-secondary-normal)';

const VISUALLY_HIDDEN_STYLES: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const S = {
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    minHeight: 0,
  },
  search: {
    marginBottom: 12,
    flexShrink: 0,
    maxWidth: 360,
  },
  entriesScroll: {
    flex: 1,
    overflowY: 'auto' as const,
    minHeight: 0,
    paddingRight: 10,
  },
  entry: {
    display: 'block',
    width: '100%',
    paddingBottom: 12,
    marginBottom: 12,
    borderBottom: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: 8,
    boxSizing: 'border-box' as const,
    outline: 'none',
    background: 'transparent',
  },
  entryLast: {
    paddingBottom: 0,
    marginBottom: 0,
    borderBottom: 'none',
  },
  speakerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  speakerName: {
    fontWeight: 'normal' as const,
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    color: TEXT1,
    flex: 1,
  },
  timestamp: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    letterSpacing: '0px',
    color: TEXT2,
    flexShrink: 0,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    letterSpacing: '0px',
    color: TEXT2,
    paddingLeft: 32, // align with text after avatar
    margin: 0,
  },
  center: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '2rem 1rem',
    color: TEXT2,
    fontSize: '0.8125rem',
  },
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || '').toUpperCase();
};

/** Format millisecond string to m:ss or h:mm:ss */
const formatMsTimestamp = (ms: string): string => {
  const totalMs = parseInt(ms, 10);
  if (isNaN(totalMs)) return ms;
  const totalSeconds = Math.floor(totalMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
};

export const CallRecapTranscriptTab: React.FC<ICallRecapTranscriptTabProps> = ({
  containerId,
}) => {
  const { t } = useTranslation('WebexCallHistory');
  const { transcriptEntries, loading } = useTranscript(containerId);
  const avatarMap = useTranscriptAvatars(transcriptEntries);
  const [searchQuery, setSearchQuery] = useState('');
  const transcriptStatusId = useId();
  const transcriptContentId = useId();
  const entryRefs = useRef<Array<HTMLElement | null>>([]);
  const unknownSpeakerLabel = t('aiSummary.unknownSpeaker', { defaultValue: 'Unknown' });

  const filtered = useMemo(() => {
    if (!searchQuery) return transcriptEntries;
    const query = searchQuery.toLowerCase();
    return transcriptEntries.filter(
      (e) => {
        const speakerName = (e.speakerName || unknownSpeakerLabel).toLowerCase();
        return e.content.toLowerCase().includes(query) || speakerName.includes(query);
      }
    );
  }, [transcriptEntries, searchQuery, unknownSpeakerLabel]);

  const transcriptStatusMessage = useMemo(() => {
    if (loading) return t('aiSummary.loadingTranscriptStatus', { defaultValue: 'Loading transcript' });
    if (!transcriptEntries.length) return t('aiSummary.noTranscriptStatus', { defaultValue: 'No transcript available' });
    if (searchQuery) {
      return filtered.length === 1
        ? t('aiSummary.oneTranscriptResult', {
            searchQuery,
            defaultValue: '1 transcript result for {{searchQuery}}',
          })
        : t('aiSummary.manyTranscriptResults', {
            count: filtered.length,
            searchQuery,
            defaultValue: '{{count}} transcript results for {{searchQuery}}',
          });
    }
    return filtered.length === 1
      ? t('aiSummary.oneTranscriptEntry', { defaultValue: '1 transcript entry' })
      : t('aiSummary.manyTranscriptEntries', {
          count: filtered.length,
          defaultValue: '{{count}} transcript entries',
        });
  }, [filtered.length, loading, searchQuery, t, transcriptEntries.length]);

  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab' && !event.shiftKey && filtered.length > 0) {
      const firstEntry = entryRefs.current[0];
      if (firstEntry) {
        event.preventDefault();
        firstEntry.focus();
      }
    }
  }, [filtered.length]);

  const focusEntryByIndex = useCallback((index: number) => {
    const target = entryRefs.current[index];
    if (target) {
      target.focus();
    }
  }, []);

  const handleEntryKeyDown = useCallback((index: number) => (event: React.KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusEntryByIndex(Math.min(index + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusEntryByIndex(Math.max(index - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        focusEntryByIndex(0);
        break;
      case 'End':
        event.preventDefault();
        focusEntryByIndex(filtered.length - 1);
        break;
      default:
        break;
    }
  }, [filtered.length, focusEntryByIndex]);

  if (loading) {
    return (
      <div style={S.center} role="status" aria-live="polite">
        <LoadingSpinner scale={32} />
        <span>{t('aiSummary.loadingTranscript', { defaultValue: 'Loading transcript...' })}</span>
      </div>
    );
  }

  if (transcriptEntries.length === 0) {
    return (
      <div style={S.center} role="status" aria-live="polite">
        <span>{t('aiSummary.noTranscriptAvailable', { defaultValue: 'No transcript available' })}</span>
      </div>
    );
  }

  return (
    <div style={S.root} data-testid="recap-transcript-tab">
      <span id={transcriptStatusId} style={VISUALLY_HIDDEN_STYLES} aria-live="polite" aria-atomic="true">
        {transcriptStatusMessage}
      </span>
      <div style={S.search}>
        <SearchInput
          style={{ width: '100%' }}
          placeholder={t('aiSummary.searchTranscript', { defaultValue: 'Search transcript' })}
          value={searchQuery}
          onChange={setSearchQuery}
          onKeyDown={handleSearchKeyDown}
          aria-label={t('aiSummary.searchTranscript', { defaultValue: 'Search transcript' })}
          aria-describedby={transcriptStatusId}
          clearButtonAriaLabel={t('aiSummary.clearTranscriptSearch', { defaultValue: 'Clear transcript search' })}
        />
      </div>

      <div
        id={transcriptContentId}
        style={S.entriesScroll}
        role="list"
        aria-label={t('aiSummary.transcriptEntries', { defaultValue: 'Transcript entries' })}
        aria-describedby={transcriptStatusId}
      >
        {filtered.length === 0 ? (
          <div style={S.center} role="status" aria-live="polite">
            <span>{t('aiSummary.noMatchingTranscriptResults', { defaultValue: 'No matching transcript results' })}</span>
          </div>
        ) : filtered.map((entry, i) => (
          <FocusRing key={i}>
            <article
              ref={(element) => {
                entryRefs.current[i] = element;
              }}
              style={i === filtered.length - 1 ? { ...S.entry, ...S.entryLast } : S.entry}
              data-testid="transcript-entry"
              role="listitem"
              tabIndex={0}
              onKeyDown={handleEntryKeyDown(i)}
              aria-label={`${entry.speakerName || unknownSpeakerLabel}, ${formatMsTimestamp(entry.timestamp)}. ${entry.content}`}
            >
              <div style={S.speakerRow}>
                <Avatar
                  initials={getInitials(entry.speakerName || unknownSpeakerLabel)}
                  title={entry.speakerName || unknownSpeakerLabel}
                  size={24}
                  src={entry.speakerEmail ? avatarMap[entry.speakerEmail] : undefined}
                />
                <span style={S.speakerName}>{entry.speakerName || unknownSpeakerLabel}</span>
                <span style={S.timestamp}>{formatMsTimestamp(entry.timestamp)}</span>
              </div>
              <p style={S.text}>{entry.content}</p>
            </article>
          </FocusRing>
        ))}
      </div>
    </div>
  );
};
